/* Guard operator definitions — shared across all guard form UI
   Maps operator keys → labels and metadata about value requirements.
   The BE evaluation logic mirrors these in stageguardservice.js. */

export type GuardType = 'field_check' | 'document_check' | 'task_check';

export type GuardOperator =
  | 'is_not_empty'
  | 'is_empty'
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'greater_than'
  | 'less_than'
  | 'between'
  | 'before'
  | 'after'
  | 'is_true'
  | 'is_false';

export interface OperatorDef {
  label: string;
  needsValue: boolean;
  needsSecondValue?: boolean;
}

export const OPERATORS: Record<GuardOperator, OperatorDef> = {
  is_not_empty: { label: 'Is not empty', needsValue: false },
  is_empty: { label: 'Is empty', needsValue: false },
  equals: { label: 'Equals', needsValue: true },
  not_equals: { label: 'Does not equal', needsValue: true },
  contains: { label: 'Contains', needsValue: true },
  not_contains: { label: 'Does not contain', needsValue: true },
  starts_with: { label: 'Starts with', needsValue: true },
  greater_than: { label: 'Greater than', needsValue: true },
  less_than: { label: 'Less than', needsValue: true },
  between: { label: 'Between', needsValue: true, needsSecondValue: true },
  before: { label: 'Before (date)', needsValue: true },
  after: { label: 'After (date)', needsValue: true },
  is_true: { label: 'Is true (checked)', needsValue: false },
  is_false: { label: 'Is false (unchecked)', needsValue: false },
};

/** Returns the applicable operator keys for a given field_type string */
export function getOperatorsForType(fieldType: string): GuardOperator[] {
  const t = (fieldType || 'string').toLowerCase();
  if (
    [
      'int',
      'integer',
      'number',
      'numeric',
      'float',
      'bigint',
      'double precision',
      'real',
      'smallint',
    ].includes(t)
  ) {
    return [
      'is_not_empty',
      'is_empty',
      'equals',
      'not_equals',
      'greater_than',
      'less_than',
      'between',
    ];
  }
  if (['date', 'datetime', 'timestamp', 'timestamptz'].includes(t)) {
    return ['is_not_empty', 'is_empty', 'equals', 'before', 'after', 'between'];
  }
  if (['boolean', 'bool'].includes(t)) {
    return ['is_not_empty', 'is_empty', 'is_true', 'is_false'];
  }
  if (['dropdown', 'select'].includes(t)) {
    return ['is_not_empty', 'is_empty', 'equals', 'not_equals'];
  }
  // Default: text / string
  return [
    'is_not_empty',
    'is_empty',
    'equals',
    'not_equals',
    'contains',
    'not_contains',
    'starts_with',
  ];
}

/** Input type for the value field, based on field_type */
export function getInputTypeForField(fieldType: string): 'text' | 'number' | 'date' {
  const t = (fieldType || '').toLowerCase();
  if (['int', 'integer', 'number', 'numeric', 'float', 'bigint'].includes(t)) return 'number';
  if (['date', 'datetime', 'timestamp', 'timestamptz'].includes(t)) return 'date';
  return 'text';
}

/** Guard type catalogue — field_check is active; others are future */
export const GUARD_TYPES: { value: GuardType; label: string; available: boolean }[] = [
  { value: 'field_check', label: 'Field Validation', available: true },
  { value: 'document_check', label: 'Document Check', available: false },
  { value: 'task_check', label: 'Task Check', available: false },
];

/** Human-readable description of a guard (used in list views) */
export function describeGuard(config: {
  field_name?: string;
  field_key?: string;
  field_column?: string;
  operator?: string;
  value?: string | null;
  value2?: string | null;
}): string {
  const field = config.field_name || config.field_key || config.field_column || '?';
  const op = (config.operator || 'is_not_empty') as GuardOperator;
  const def = OPERATORS[op];
  if (!def) return `${field} — unknown condition`;
  if (!def.needsValue) return `${field} ${def.label.toLowerCase()}`;
  if (def.needsSecondValue)
    return `${field} ${def.label.toLowerCase()} ${config.value ?? ''} and ${config.value2 ?? ''}`;
  return `${field} ${def.label.toLowerCase()} "${config.value ?? ''}"`;
}
